import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  MENU_CONFIG,
  SOCIAL_MEDIA_URLS,
  APP_STORE_URLS,
  CONTACT_INFO,
  AGENCY_INFO,
} from "../../constants";
import {
  FooterWrapper,
  Container,
  MainContent,
  LogoSection,
  Description,
  MenuSectionsWrapper,
  MenuSection,
  SectionTitle,
  MenuList,
  MenuListItem,
  MenuLink,
  MenuPlaceholder,
  Divider,
  BottomSection,
  PartnerLogos,
  PartnerLogo,
  ContactSocialSection,
  ContactInfo,
  SocialInfo,
  ContactItem,
  SocialItem,
  ContactLink,
  ContactText,
  AppStoreSection,
  AppButton,
  AppButtonImage,
  FinalBottom,
  CopyrightSection,
  CopyrightText,
  AgencyLogo,
  PrivacyLink,
  LogoLink,
} from "./NewFooter.styles";

// Assets
import logo from "../../images/newLogo2.png";
import appstore from "../../images/appstore.svg";
import googleplay from "../../images/googleplay.svg";
import onmtLogo from "../../images/logom.png"; // Partner logo
import rfmLogo from "../../images/logo-rfm.png"; // Partner logo
import casbahLogo from "../../images/casbah.svg"; // Agency logo
import { useFetchCities } from "../../hooks/useFetchCities";

function NewFooter() {
  const history = useHistory();
  const { t } = useTranslation();
  const { local } = useSelector((state) => state.Local);
  const { data: cities = [], isLoading: isLoadingCities } = useFetchCities();

  const destinations = useMemo(
    () =>
      cities
        .filter((city) => city.slug !== "volubilis")
        .map((city) => ({
          label: city.title[local] ? city.title[local] : city.title["en"],
          path: `/${local}/destination/${city.id}`,
          labelKey: city.slug,
          onClick: () => {
            history.push(`/${local}/destination/${city.id}`);
          },
        })),
    [cities, history, local]
  );

  const destinationPlaceholders = useMemo(
    () => Array.from({ length: 9 }, (_, index) => index),
    []
  );

  const handleItemClick = (item) => {
    if (item.external && item.url) {
      window.open(item.url, "_blank");
    } else if (item.onClick) {
      item.onClick(); // Use the item's onClick function (for cities)
    } else {
      history.push(`/${local}${item.path}`); // Fallback for other menu items
    }
  };

  const handleSocialClick = (platform) => {
    window.open(SOCIAL_MEDIA_URLS[platform], "_blank");
  };

  const handleAppStoreClick = (store) => {
    window.open(APP_STORE_URLS[store], "_blank");
  };

  return (
    <FooterWrapper>
      <Container>
        {/* Main Content Grid */}
        <MainContent>
          {/* Logo & Description */}
          <LogoSection>
            <LogoLink to={`/${local}`}>
              <img loading="lazy" src={logo} alt={t("CRT")} />
            </LogoLink>

            <Description>
              {t("footerDescription") ||
                "Discover the authentic beauty of Fez-Meknes region. From historic medinas to pristine nature, plan your perfect Moroccan adventure with us."}
            </Description>
          </LogoSection>

          {/* Menu Sections Wrapper for Mobile Flex */}
          <MenuSectionsWrapper>
            {/* Destinations */}
            <MenuSection>
              <SectionTitle>{t("destinations")}</SectionTitle>
              <MenuList>
                {isLoadingCities && destinations.length === 0
                  ? destinationPlaceholders.map((placeholderKey) => (
                      <MenuListItem
                        key={`destination-placeholder-${placeholderKey}`}
                        aria-hidden="true"
                      >
                        <MenuPlaceholder />
                      </MenuListItem>
                    ))
                  : destinations.map((item, index) => (
                      <MenuListItem key={item.labelKey || index}>
                        <MenuLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleItemClick(item);
                          }}
                        >
                          {item.label}
                        </MenuLink>
                      </MenuListItem>
                    ))}
              </MenuList>
            </MenuSection>

            {/* Things To Do */}
            <MenuSection>
              <SectionTitle>{t("thingsToDo")}</SectionTitle>
              <MenuList>
                {MENU_CONFIG.thingsToDo.items.map((item, index) => (
                  <MenuListItem key={index}>
                    <MenuLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleItemClick(item);
                      }}
                    >
                      {t(item.labelKey)}
                    </MenuLink>
                  </MenuListItem>
                ))}
              </MenuList>
            </MenuSection>

            {/* Plan */}
            <MenuSection>
              <SectionTitle>{t("plan")}</SectionTitle>
              <MenuList>
                {MENU_CONFIG.plan.items.map((item, index) => (
                  <MenuListItem key={index}>
                    <MenuLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleItemClick(item);
                      }}
                    >
                      {t(item.labelKey)}
                    </MenuLink>
                  </MenuListItem>
                ))}
              </MenuList>
            </MenuSection>

            {/* Info */}
            <MenuSection>
              <SectionTitle>{t("info")}</SectionTitle>
              <MenuList>
                {MENU_CONFIG.info.items.map((item, index) => (
                  <MenuListItem key={index}>
                    <MenuLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleItemClick(item);
                      }}
                    >
                      {t(item.labelKey)}
                    </MenuLink>
                  </MenuListItem>
                ))}
              </MenuList>
            </MenuSection>
          </MenuSectionsWrapper>
        </MainContent>

        {/* Divider */}
        <Divider />

        {/* Bottom Section */}
        <BottomSection>
          {/* Partner Logos */}
          <PartnerLogos>
            <PartnerLogo
              src={rfmLogo}
              alt="Région Fès-Meknès"
              loading="lazy"
              $width={68}
              $height={68}
            />
            <PartnerLogo
              src={logo}
              alt={t("logoAlt")}
              onClick={() => history.push(`/${local}`)}
              loading="lazy"
              $width={180}
              $height={68}
            />
            <PartnerLogo
              src={onmtLogo}
              alt="ONMT"
              loading="lazy"
              $width={180}
              $height={68}
            />
          </PartnerLogos>

          {/* Contact & Social */}
          <ContactSocialSection>
            <ContactInfo>
              <ContactItem>
                <ContactLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    history.push(`/${local}${CONTACT_INFO.contactPath}`);
                  }}
                  $underline
                >
                  {t("contactUs") || "Contact Us"}
                </ContactLink>
              </ContactItem>
              <ContactItem>
                <ContactLink href={`mailto:${CONTACT_INFO.email}`}>
                  {CONTACT_INFO.email}
                </ContactLink>
              </ContactItem>
              <ContactItem>
                <ContactText>{CONTACT_INFO.phone}</ContactText>
              </ContactItem>
            </ContactInfo>

            <SocialInfo>
              <SocialItem>
                <ContactLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick("facebook");
                  }}
                >
                  Facebook
                </ContactLink>
              </SocialItem>
              <SocialItem>
                <ContactLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick("instagram");
                  }}
                >
                  Instagram
                </ContactLink>
              </SocialItem>
              <SocialItem>
                <ContactLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick("youtube");
                  }}
                >
                  YouTube
                </ContactLink>
              </SocialItem>
            </SocialInfo>
          </ContactSocialSection>

          {/* App Store Buttons */}
          <AppStoreSection>
            <AppButton onClick={() => handleAppStoreClick("appstore")}>
              <AppButtonImage
                loading="lazy"
                src={appstore}
                alt="Download on App Store"
              />
            </AppButton>
            <AppButton onClick={() => handleAppStoreClick("googleplay")}>
              <AppButtonImage
                loading="lazy"
                src={googleplay}
                alt="Get it on Google Play"
              />
            </AppButton>
          </AppStoreSection>
        </BottomSection>

        {/* Final Bottom */}
        <FinalBottom>
          <CopyrightSection>
            <CopyrightText>
              © {new Date().getFullYear()}{" "}
              {t("copyrightFooter") || "All rights reserved"}
            </CopyrightText>
            <a
              href={AGENCY_INFO.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-block" }}
            >
              <AgencyLogo loading="lazy" src={casbahLogo} alt="CASBAH" />
            </a>
            <PrivacyLink
              href={`/${local}/policy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("privacyPolicy") || "Privacy Policy"}
            </PrivacyLink>
          </CopyrightSection>
        </FinalBottom>
      </Container>
    </FooterWrapper>
  );
}

export default NewFooter;
